import React from 'react'

import { Dropdown } from '@sketch/components'
import {
  ProjectInSidebarAndHeaderFragment,
  ProjectListItemFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useFlag } from '@sketch/modules-common'

import {
  CopyProjectAction,
  CreateCollectionAction,
  DeleteProjectAction,
  LeaveProjectAction,
  RenameProjectAction,
  ShareProjectAction,
  PinProjectAction,
} from '../ProjectDropdownActions'

interface ProjectDropdownProps {
  workspace: WorkspaceMinimalFragment
  project: ProjectInSidebarAndHeaderFragment | ProjectListItemFragment
  onRename: () => void
}

const ProjectDropdown = (props: ProjectDropdownProps) => {
  const { workspace, project: baseProject, onRename } = props
  const workspaceId = props.workspace.identifier

  const hasGuestsInProjects = useFlag('guests-in-project')
  const isPinningEnabled = useFlag('pinning')
  /**
   * This workaround was added because "ProjectListItemFragment" crashes
   * with the type "Share". "type" and "privacyIcon" have different types on Share
   * therefore we need to rename them
   */
  const project = {
    ...baseProject,
    privacyIcon:
      'projectPrivacyIcon' in baseProject
        ? baseProject.projectPrivacyIcon
        : baseProject.privacyIcon,
    type:
      'projectType' in baseProject ? baseProject.projectType : baseProject.type,
  }
  const pinned = project.pinnedByCurrentUserAt

  const { userCanEdit, userCanAdminister } = workspace

  // only editors and admins can perform the following actions:
  //   - Add/remove people from the project (<ShareProjectAction .../>)
  //   - Rename the project (<RenameProjectAction .../>)
  //   - Delete the project (<DeleteProjectAction .../>)
  const showEditActions = userCanEdit || userCanAdminister
  // The `Leave...` button is being added as part of the guests-in-projects feature,
  // only users with the feature flag enabled AND who can leave the project
  // should see the button.
  const showLeaveAction = hasGuestsInProjects && project.userCanLeave
  const showDivider = showEditActions || showLeaveAction

  return (
    <>
      <Dropdown.Header>Project</Dropdown.Header>
      {showEditActions && (
        <ShareProjectAction project={project} workspace={workspace} />
      )}
      {showEditActions && <RenameProjectAction onRename={onRename} />}
      {showEditActions && (
        <CreateCollectionAction
          project={project}
          workspaceIdentifier={workspaceId}
        />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {isPinningEnabled && (
        <PinProjectAction
          projectIdentifier={project.identifier}
          pinned={pinned}
          workspaceId={workspaceId}
        />
      )}
      {showDivider && <Dropdown.Divider />}
      {showLeaveAction && (
        <LeaveProjectAction
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )
}

export default ProjectDropdown
